// Phase 19 · Community V2 · Groups directory
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Users, Plus, Hash, Search } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import NotificationBell from "../../components/community_v2/NotificationBell";
import UpsellBanner from "@/components/upsell/UpsellBanner";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
    { slug: "", name: "All" },
    { slug: "marketing", name: "Marketing" },
    { slug: "automazioni", name: "Automazioni" },
    { slug: "ai-tools", name: "AI Tools" },
    { slug: "funnel", name: "Funnel" },
    { slug: "seo", name: "SEO" },
    { slug: "template", name: "Template" },
    { slug: "creator", name: "Creator" },
    { slug: "aziende", name: "Aziende" },
];

function GroupCard({ g }) {
    return (
        <Link
            to={`/groups/${g.id}`}
            className="block bg-[#111111] border border-[#1A1A1A] rounded-xl p-4 hover:border-[#FFFFFF]/40 transition-all"
            data-testid={`group-card-${g.slug}`}
        >
            <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center text-neutral-900 font-bold flex-shrink-0">
                    {g.name?.[0]?.toUpperCase() || "G"}
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-neutral-100 truncate">
                        {g.name}
                    </h3>
                    <div className="flex items-center gap-2 text-[10px] text-neutral-500 mt-0.5">
                        <span className="inline-flex items-center gap-0.5">
                            <Hash size={10} />
                            {g.category_slug}
                        </span>
                        <span>·</span>
                        <span className="inline-flex items-center gap-0.5">
                            <Users size={10} />
                            {g.member_count || 1}
                        </span>
                        <span>·</span>
                        <span>{g.channel_count || 0} channels</span>
                    </div>
                </div>
            </div>
            {g.description && (
                <p className="mt-2 text-xs text-neutral-400 line-clamp-2">
                    {g.description}
                </p>
            )}
        </Link>
    );
}

function CreateGroupModal({ onClose, onCreated, flags }) {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [category, setCategory] = useState("marketing");
    const [visibility, setVisibility] = useState("public");
    const [orgId, setOrgId] = useState("");
    const [busy, setBusy] = useState(false);

    const showPrivate = !!flags?.COMMUNITY_PRIVATE_ENABLED;
    const showCorporate = !!flags?.COMMUNITY_CORPORATE_ENABLED;
    const anyVisibilityOn = showPrivate || showCorporate;

    const submit = async () => {
        if (name.trim().length < 3) { toast.error("Name needs 3+ chars"); return; }
        if (visibility === "corporate" && !orgId.trim()) {
            toast.error("org_id required for corporate visibility"); return;
        }
        setBusy(true);
        try {
            const body = {
                name: name.trim(),
                description,
                category_slug: category,
                visibility,
            };
            if (visibility === "corporate") body.org_id = orgId.trim();
            const r = await fetch(`${API}/api/community/groups`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(body),
            });
            if (!r.ok) {
                const err = await r.json().catch(() => ({}));
                if (r.status === 429) toast.error("You can create 1 group per day");
                else if (r.status === 503) toast.error(err.detail?.error || "Feature disabled");
                else toast.error(err.detail?.error || "Group creation failed");
                return;
            }
            const d = await r.json();
            toast.success("Group created");
            onCreated?.(d);
        } catch { toast.error("Network error"); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="create-group-modal">
            <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl w-full max-w-md p-6">
                <h2 className="text-lg font-semibold text-neutral-100 mb-4">Create new group</h2>
                <label className="block text-xs text-neutral-400 mb-1">Name</label>
                <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={50}
                    className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF] mb-3"
                    data-testid="group-name-input"
                />
                <label className="block text-xs text-neutral-400 mb-1">Category</label>
                <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF] mb-3"
                    data-testid="group-category-input"
                >
                    {CATEGORIES.filter(c => c.slug).map(c => (
                        <option key={c.slug} value={c.slug}>{c.name}</option>
                    ))}
                </select>
                <label className="block text-xs text-neutral-400 mb-1">Description</label>
                <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    maxLength={500}
                    className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF] mb-4"
                    data-testid="group-description-input"
                />
                {anyVisibilityOn && (
                    <>
                        <label className="block text-xs text-neutral-400 mb-1">Visibility</label>
                        <select
                            value={visibility}
                            onChange={(e) => setVisibility(e.target.value)}
                            className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF] mb-3"
                            data-testid="group-visibility-input"
                        >
                            <option value="public">Public — anyone can discover + join</option>
                            {showPrivate && <option value="private">Private — invite / approval only</option>}
                            {showCorporate && <option value="corporate">Corporate — organization members only</option>}
                        </select>
                        {visibility === "corporate" && (
                            <>
                                <label className="block text-xs text-neutral-400 mb-1">Organization ID</label>
                                <input
                                    type="text"
                                    value={orgId}
                                    onChange={(e) => setOrgId(e.target.value)}
                                    placeholder="uuid of community_orgs entry"
                                    className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF] mb-4"
                                    data-testid="group-org-id-input"
                                />
                            </>
                        )}
                    </>
                )}
                <div className="flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-neutral-400 hover:text-neutral-200">Cancel</button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy}
                        className="px-4 py-2 text-sm bg-white text-neutral-900 font-semibold rounded disabled:opacity-40"
                        data-testid="group-create-submit"
                    >
                        {busy ? "..." : "Create"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function GroupsDirectory() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [category, setCategory] = useState("");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [flags, setFlags] = useState({});

    useEffect(() => {
        fetch(`${API}/api/community/flags`, { credentials: "include" })
            .then((r) => r.ok ? r.json() : {})
            .then(setFlags).catch(() => {});
    }, []);

    const load = useCallback(async () => {
        setLoading(true); setDisabled(false);
        try {
            const params = new URLSearchParams();
            if (category) params.set("category", category);
            if (search) params.set("search", search);
            params.set("limit", "40");
            const r = await fetch(`${API}/api/community/groups?${params}`, { credentials: "include" });
            if (r.status === 503) { setDisabled(true); setItems([]); setTotal(0); return; }
            if (r.ok) {
                const d = await r.json();
                setItems(d.items || []);
                setTotal(d.total || 0);
            }
        } catch {}
        setLoading(false);
    }, [category, search]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter" data-testid="groups-directory-page">
            <header className="border-b border-[#1A1A1A] backdrop-blur-md bg-[#0A0A0A]/80 sticky top-0 z-40">
                <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link to="/community" className="text-neutral-400 hover:text-neutral-200 flex items-center gap-1 text-sm">
                            <ArrowLeft size={16} /> Community
                        </Link>
                        <span className="text-neutral-600">·</span>
                        <h1 className="text-base font-semibold flex items-center gap-2">
                            <Users size={16} className="text-[#FFFFFF]" /> Groups
                        </h1>
                    </div>
                    <div className="flex items-center gap-2">
                        {user && (
                            <button
                                type="button"
                                onClick={() => setCreating(true)}
                                className="inline-flex items-center gap-2 bg-white text-neutral-900 font-semibold px-3 py-2 rounded-lg text-sm"
                                data-testid="create-group-btn"
                            >
                                <Plus size={14} /> Create group
                            </button>
                        )}
                        {user && <NotificationBell enabled />}
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 py-6">
                <UpsellBanner surface="community_groups" />
                {disabled && (
                    <div className="bg-[#FAFAFA]/10 border border-[#FAFAFA]/30 rounded-lg p-4 text-sm text-[#FAFAFA] mb-4">
                        Groups disabled by admin.
                    </div>
                )}
                <div className="flex flex-wrap items-center gap-2 mb-6 text-xs">
                    <div className="flex-1 min-w-[200px] relative">
                        <Search size={14} className="absolute left-3 top-2.5 text-neutral-500" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search groups…"
                            className="w-full bg-[#111111] border border-[#1A1A1A] rounded-lg pl-9 pr-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                            data-testid="groups-search-input"
                        />
                    </div>
                    {CATEGORIES.map(c => (
                        <button
                            key={c.slug || "all"}
                            type="button"
                            onClick={() => setCategory(c.slug)}
                            className={`px-3 py-1.5 rounded-full border transition-all ${category === c.slug ? "bg-[#FFFFFF]/10 border-[#FFFFFF]/40 text-[#FFFFFF]" : "border-[#1A1A1A] text-neutral-400 hover:border-[#FFFFFF]/30"}`}
                            data-testid={`groups-cat-${c.slug || "all"}`}
                        >
                            {c.name}
                        </button>
                    ))}
                </div>

                <p className="text-xs text-neutral-500 mb-3">{total} groups</p>
                {loading && <p className="text-sm text-neutral-500">Loading…</p>}
                {!loading && !items.length && !disabled && (
                    <p className="text-sm text-neutral-500 italic">No groups yet</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {items.map(g => <GroupCard key={g.id} g={g} />)}
                </div>
            </main>

            {creating && (
                <CreateGroupModal
                    onClose={() => setCreating(false)}
                    onCreated={(g) => { setCreating(false); load(); }}
                    flags={flags}
                />
            )}
        </div>
    );
}
